import { useEffect, useLayoutEffect } from 'react';
import { atom, useSetAtom } from 'jotai';

// Client: layout effect so tile children read tracking config on first paint.
// Server: useEffect avoids the useLayoutEffect SSR warning.
const useIsomorphicLayoutEffect =
    typeof window !== 'undefined' ? useLayoutEffect : useEffect;

export interface TilesTrackingConfig {
    /**
     * Callback fired when the tile is selected (clicked).
     */
    onClick?: (
        targetRef: React.RefObject<HTMLElement | null>, // we need it for calculating the tile position
        itemID?: number,
        professionalID?: number,
        section?: string,
    ) => void;

    /**
     * Callback fired when the tile is hovered (mouseenter with debounce).
     */
    onHover?: (
        targetRef: React.RefObject<HTMLElement>,
        itemID?: number,
        professionalID?: number,
        section?: string,
    ) => void;

    /**
     * Callback fired when the category is selected (clicked).
     */
    onCategoryClick?: (categoryID: number) => void;

    onStaticTileClick?: (title: string) => void;

    onTileMediaSliderSlideChange?: (
        direction: 'CarouselPrev' | 'CarouselNext',
        itemID?: number,
    ) => void;

    /**
     * Sets a project's banner to one of its slide images, by reference.
     * Injected by the web layer; consumed by ProjectTile's banner checkbox.
     * Per-call callbacks let each tile drive its own optimistic state.
     */
    onSetBanner?: (input: {
        libraryItemID: number;
        libraryImageID: number;
        onError: () => void;
        onSuccess?: () => void;
    }) => void;
}

/**
 * Context interface for managing tiles actions and interactions.
 * Provides methods for tracking tile configurations.
 * @interface TileActionsContextType
 */
interface TileActionsContextType {
    trackingConfig?: TilesTrackingConfig;
}

/**
 * Atoms for managing tiles actions context.
 * `localContextAtom` stores the core context data privately.
 * `uiContextAtomTilesActions` provides read-only access to the context data.
 */
export const uiContextAtomTileActions = atom((get) => get(localContextAtom));
const localContextAtom = atom<TileActionsContextType>();

interface UseSharedTilesProps {
    trackingConfig?: TilesTrackingConfig;
}

export const useSharedTiles = (props: UseSharedTilesProps) => {
    const { trackingConfig } = props;
    const setContext = useSetAtom(localContextAtom);

    // Isomorphic layout effect: on the client, runs before paint so tile children
    // read onSetBanner on first paint (a post-paint useEffect hid the banner checkbox
    // until a second render).
    useIsomorphicLayoutEffect(() => {
        if (!trackingConfig) return;
        setContext({ trackingConfig });

        // Clear on the way out, or a page's callbacks outlive it: the atom is
        // module-level and lives for the whole SPA session, so navigating to a
        // route that mounts no writer left the previous page still handling
        // every tile click, attributed to a surface the reader had left.
        //
        // Only if this config is still the one in the atom — a route with two
        // writers (a page and the grid inside it) must not have the first to
        // unmount wipe the second's.
        return () => {
            setContext((current) =>
                current?.trackingConfig === trackingConfig
                    ? undefined
                    : current,
            );
        };
    }, [setContext, trackingConfig]);
};
